
import { useState, useEffect } from "react";
import { useSession } from "@supabase/auth-helpers-react";
import { supabase } from "@/integrations/supabase/client";

interface PrayerRequest {
  id: number;
}

export function usePrayerLikes(prayerRequests: PrayerRequest[]) {
  const session = useSession();
  const [likes, setLikes] = useState<Record<number, number>>({});
  const [userLikes, setUserLikes] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetchLikes();
  }, [prayerRequests]);

  const fetchLikes = async () => {
    try {
      const likeCounts = await Promise.all(
        prayerRequests.map(async (request) => {
          const { count } = await supabase
            .from('prayer_likes')
            .select('*', { count: 'exact' })
            .eq('prayer_request_id', request.id);
          return { id: request.id, count: count || 0 };
        })
      );

      if (session?.user) {
        const { data: userLikesData } = await supabase
          .from('prayer_likes')
          .select('prayer_request_id')
          .eq('user_id', session.user.id);

        const userLikesMap = (userLikesData || []).reduce((acc, like) => ({
          ...acc,
          [like.prayer_request_id]: true
        }), {});
        setUserLikes(userLikesMap);
      }

      const likesMap = likeCounts.reduce((acc, { id, count }) => ({
        ...acc,
        [id]: count
      }), {});
      setLikes(likesMap);
    } catch (error) {
      console.error('Error fetching likes:', error);
    }
  };

  const handleLike = async (requestId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!session?.user) {
      return false;
    }

    try {
      if (userLikes[requestId]) {
        await supabase
          .from('prayer_likes')
          .delete()
          .eq('prayer_request_id', requestId)
          .eq('user_id', session.user.id);
        
        setLikes(prev => ({
          ...prev,
          [requestId]: (prev[requestId] || 1) - 1
        }));
        setUserLikes(prev => ({
          ...prev,
          [requestId]: false
        }));
      } else {
        await supabase
          .from('prayer_likes')
          .insert({
            prayer_request_id: requestId,
            user_id: session.user.id
          });
        
        setLikes(prev => ({
          ...prev,
          [requestId]: (prev[requestId] || 0) + 1
        }));
        setUserLikes(prev => ({
          ...prev,
          [requestId]: true
        }));
      }
      return true;
    } catch (error) {
      console.error('Error handling like:', error);
      return false;
    }
  };

  return {
    likes,
    userLikes,
    handleLike
  };
}
